// @ts-ignore - react-plotly.js doesn't have proper TypeScript types
import { useState } from 'react'
import Plot from 'react-plotly.js'
import { AnnotationConsistencyMetrics, KeypointInferenceStats } from '../../types'
import SequentialImageAnalysis from './SequentialImageAnalysis'

interface ConsistencyChartsProps {
  metrics: AnnotationConsistencyMetrics[]
  meanBboxStd: number
  meanVisibilityMetric: number
  meanKeypointStd: number
  inferenceStats?: KeypointInferenceStats[]
  keypointNames?: string[]
  analysisMode?: 'sequential' | 'independent'
}

export default function ConsistencyCharts({
  metrics,
  meanBboxStd,
  meanVisibilityMetric,
  meanKeypointStd,
  inferenceStats,
  keypointNames,
  analysisMode = 'sequential',
}: ConsistencyChartsProps) {
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null)
  const [selectedObjectIndex, setSelectedObjectIndex] = useState<number | null>(null)

  if (!metrics || metrics.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">
          No consistency metrics available. Need at least 2 annotators per image.
        </p>
      </div>
    )
  }

  // Filter metrics based on selected filters
  const filteredMetrics = metrics.filter(m => {
    if (selectedImageId && m.image_id !== selectedImageId) return false
    if (selectedObjectIndex !== null && m.object_index !== selectedObjectIndex) return false
    return true
  })

  // Prepare data for charts
  const annotationIds = filteredMetrics.map((_, idx) => idx)

  // Create labels that show object information
  const annotationLabels = filteredMetrics.map((m, idx) => {
    if (m.total_objects_in_image && m.total_objects_in_image > 1) {
      return `${idx} (Obj ${(m.object_index || 0) + 1}/${m.total_objects_in_image})`
    }
    return idx.toString()
  })

  // Bbox STD data
  const bboxData = {
    x: annotationIds,
    y: filteredMetrics.map(m => m.bbox_std),
    type: 'bar' as const,
    marker: { color: '#3b82f6' },
    name: 'Bbox STD',
    text: annotationLabels,
    hovertemplate: 'ID: %{text}<br>Bbox STD: %{y:.2f}%<br>Image: %{customdata}<extra></extra>',
    customdata: filteredMetrics.map(m => m.image_id.substring(0, 8)),
  }

  const bboxMeanLine = {
    x: [-0.5, filteredMetrics.length - 0.5],
    y: [meanBboxStd, meanBboxStd],
    type: 'scatter' as const,
    mode: 'lines',
    line: { color: '#ef4444', dash: 'dash', width: 2 },
    name: `Mean: ${meanBboxStd.toFixed(2)}%`,
    hovertemplate: 'Mean: %{y:.2f}%<extra></extra>',
  }

  // Visibility data
  const visibilityData = {
    x: annotationIds,
    y: filteredMetrics.map(m => m.visibility_metric),
    type: 'bar' as const,
    marker: { color: '#f59e0b' },
    name: 'Visibility Metric',
    text: annotationLabels,
    hovertemplate: 'ID: %{text}<br>Visibility: %{y:.2f}<br>Image: %{customdata}<extra></extra>',
    customdata: filteredMetrics.map(m => m.image_id.substring(0, 8)),
  }

  const visibilityMeanLine = {
    x: [-0.5, filteredMetrics.length - 0.5],
    y: [meanVisibilityMetric, meanVisibilityMetric],
    type: 'scatter' as const,
    mode: 'lines',
    line: { color: '#ef4444', dash: 'dash', width: 2 },
    name: `Mean: ${meanVisibilityMetric.toFixed(2)}`,
    hovertemplate: 'Mean: %{y:.2f}<extra></extra>',
  }

  // Keypoint STD data
  const keypointData = {
    x: annotationIds,
    y: filteredMetrics.map(m => m.keypoint_std),
    type: 'bar' as const,
    marker: { color: '#10b981' },
    name: 'Keypoint STD',
    text: annotationLabels,
    hovertemplate: 'ID: %{text}<br>Keypoint STD: %{y:.2f}%<br>Image: %{customdata}<extra></extra>',
    customdata: filteredMetrics.map(m => m.image_id.substring(0, 8)),
  }

  const keypointMeanLine = {
    x: [-0.5, filteredMetrics.length - 0.5],
    y: [meanKeypointStd, meanKeypointStd],
    type: 'scatter' as const,
    mode: 'lines',
    line: { color: '#ef4444', dash: 'dash', width: 2 },
    name: `Mean: ${meanKeypointStd.toFixed(2)}%`,
    hovertemplate: 'Mean: %{y:.2f}%<extra></extra>',
  }

  // Common layout settings
  const commonLayout = {
    showlegend: false,
    margin: { l: 50, r: 30, t: 40, b: 40 },
    height: 250,
    xaxis: {
      title: 'Annotation ID',
      range: [-0.5, filteredMetrics.length - 0.5],
    },
    hovermode: 'closest' as const,
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
  }

  return (
    <div className="space-y-6">
      {/* Sequential mode analysis */}
      <div className="mb-6">
        <SequentialImageAnalysis metrics={metrics} selectedObjectIndex={selectedObjectIndex} />
      </div>

      {/* Frame-by-Frame Analysis */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Frame-by-Frame Analysis</h3>
          <p className="text-sm text-gray-600 mt-1">
            Detailed consistency metrics for each frame in the sequence.
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          {/* Object filter */}
          {metrics.some(m => m.total_objects_in_image && m.total_objects_in_image > 1) && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Filter by Object:</label>
              <select
                value={selectedObjectIndex !== null ? selectedObjectIndex : ''}
                onChange={e =>
                  setSelectedObjectIndex(e.target.value ? parseInt(e.target.value) : null)
                }
                className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">All Objects</option>
                {Array.from(new Set(metrics.map(m => m.object_index || 0)))
                  .sort((a, b) => a - b)
                  .map(objIdx => (
                    <option key={objIdx} value={objIdx}>
                      Object {objIdx + 1} ({metrics.filter(m => m.object_index === objIdx).length}{' '}
                      frames)
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Image filter for multi-object images */}
          {metrics.some(m => m.total_objects_in_image && m.total_objects_in_image > 1) && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Filter by Image:</label>
              <select
                value={selectedImageId || ''}
                onChange={e => setSelectedImageId(e.target.value || null)}
                className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">All Images</option>
                {Array.from(
                  new Set(
                    metrics
                      .filter(m => m.total_objects_in_image && m.total_objects_in_image > 1)
                      .map(m => m.image_id)
                  )
                ).map(imageId => (
                  <option key={imageId} value={imageId}>
                    {imageId.substring(0, 8)}... (
                    {metrics.filter(m => m.image_id === imageId).length} objects)
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bbox STD Chart */}
        <div className="bg-white rounded-lg shadow p-4">
          <Plot
            data={[bboxData, bboxMeanLine]}
            layout={{
              ...commonLayout,
              title: 'Bbox Standard Deviation (%)',
              yaxis: { title: 'Std of bbox (%)' },
            }}
            config={{ displayModeBar: false }}
            style={{ width: '100%' }}
          />
          <p className="text-sm text-gray-600 mt-2 text-center">
            Lower values indicate better consistency
          </p>
        </div>

        {/* Visibility Chart */}
        <div className="bg-white rounded-lg shadow p-4">
          <Plot
            data={[visibilityData, visibilityMeanLine]}
            layout={{
              ...commonLayout,
              title: 'Visibility Metric',
              yaxis: { title: 'Visibility Metric' },
            }}
            config={{ displayModeBar: false }}
            style={{ width: '100%' }}
          />
          <p className="text-sm text-gray-600 mt-2 text-center">
            Lower values indicate better visibility agreement
          </p>
        </div>

        {/* Keypoint STD Chart */}
        <div className="bg-white rounded-lg shadow p-4">
          <Plot
            data={[keypointData, keypointMeanLine]}
            layout={{
              ...commonLayout,
              title: 'Keypoint Standard Deviation (%)',
              yaxis: { title: 'Std of keypoints (%)' },
            }}
            config={{ displayModeBar: false }}
            style={{ width: '100%' }}
          />
          <p className="text-sm text-gray-600 mt-2 text-center">
            Lower values indicate better keypoint consistency
          </p>
        </div>
      </div>

      {/* Consistency Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h4 className="text-sm font-medium text-gray-900">Detailed Consistency Metrics</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Annotation ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Object
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Annotators
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bbox STD (%)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visibility Metric
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Keypoint STD (%)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMetrics.map(metric => (
                <tr key={metric.annotation_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {metric.annotation_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {metric.image_id.substring(0, 8)}...
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {metric.total_objects_in_image && metric.total_objects_in_image > 1 ? (
                      <span className="font-medium">
                        {(metric.object_index || 0) + 1} of {metric.total_objects_in_image}
                      </span>
                    ) : (
                      <span className="text-gray-400">Single</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {metric.annotator_count || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {metric.bbox_std.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {metric.visibility_metric.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {metric.keypoint_std.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Inference Statistics */}
      {inferenceStats && inferenceStats.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">Keypoint Inference Analysis</h4>
            {analysisMode === 'sequential' && selectedObjectIndex !== null && (
              <span className="text-sm text-gray-600">
                Showing data for Object {selectedObjectIndex + 1}
              </span>
            )}
          </div>

          {/* Stacked Bar Chart for Visibility Distribution */}
          <div className="mb-6">
            <Plot
              data={[
                {
                  x: inferenceStats.map(stat =>
                    keypointNames && keypointNames[stat.keypoint_index]
                      ? `${stat.keypoint_index + 1}. ${keypointNames[stat.keypoint_index]}`
                      : `Keypoint ${stat.keypoint_index + 1}`
                  ),
                  y: inferenceStats.map(stat => stat.visible_percentage),
                  name: 'Visible',
                  type: 'bar' as const,
                  marker: { color: '#10b981' },
                },
                {
                  x: inferenceStats.map(stat =>
                    keypointNames && keypointNames[stat.keypoint_index]
                      ? `${stat.keypoint_index + 1}. ${keypointNames[stat.keypoint_index]}`
                      : `Keypoint ${stat.keypoint_index + 1}`
                  ),
                  y: inferenceStats.map(stat => stat.inferred_percentage),
                  name: 'Inferred',
                  type: 'bar' as const,
                  marker: { color: '#f59e0b' },
                },
              ]}
              layout={{
                title: 'Keypoint Visibility Distribution (%)',
                barmode: 'stack',
                xaxis: {
                  title: 'Keypoints',
                  tickangle: -45,
                },
                yaxis: {
                  title: 'Percentage (%)',
                  range: [0, 100],
                },
                height: 400,
                margin: { l: 50, r: 30, t: 60, b: 120 },
                hovermode: 'closest' as const,
                showlegend: true,
                legend: { orientation: 'h', y: -0.3 },
              }}
              config={{ displayModeBar: false }}
              style={{ width: '100%' }}
            />
          </div>

          {/* Inference Statistics Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Keypoint
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Not Labeled
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Inferred
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visible
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Inference Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {inferenceStats.map(stat => (
                  <tr key={stat.keypoint_index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {keypointNames && keypointNames[stat.keypoint_index]
                        ? `${stat.keypoint_index + 1}. ${keypointNames[stat.keypoint_index]}`
                        : `Keypoint ${stat.keypoint_index + 1}`}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stat.not_labeled_count}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stat.inferred_count}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stat.visible_count}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span
                        className={`font-medium ${stat.inferred_percentage > 50 ? 'text-orange-600' : ''}`}
                      >
                        {stat.inferred_percentage.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
